import { configApi, setBearerToken } from '../../../../shared/utils/http-client.js'
import { loginApi, ssoLoginApi, refreshTokenApi, currentUserDetailsApi } from '../../../../shared/utils/api/login.js'
import router from '../../router/index.js'
import { changeUserBrandIds } from '../../../../shared/state/brands.js'
import { parseToken } from '../../../../shared/utils/parse-token.js'
import { decideSessionTick } from '../../../../shared/utils/session-sync.js'
import { customLogoutUrl } from '../../../../shared/state/configuration.js'
import { SESSION_EXPIRED } from '../../router/route-names.js'
const localStorageUserKey = 'auth.currentUser'

const getCookieValue = name =>
	document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''

function saveState (key, state) {
	window.localStorage.setItem(key, JSON.stringify(state))
}

function setTokenCookie (token) {
	if (!token) {
		document.cookie = 'cs-portal-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
	} else {
		const parsedToken = parseToken(token)
		const expiry = new Date(0)
		expiry.setUTCSeconds(parsedToken.exp)
		document.cookie =
		'cs-portal-token=' + token + '; expires=' + expiry.toUTCString() + '; path=/; secure;'
	}
}

export const state = {
	currentUser: JSON.parse(window.localStorage.getItem(localStorageUserKey)),
	userDetails: {},
	sessionWarning: false,
	countdown: 0,
	insufficientAccess: false,
	insufficientAccessReason: null,
	isRefreshingToken: false
}

let sessionCheckInterval

export const mutations = {
	SET_CURRENT_USER (state, user) {
		state.currentUser = user
		if (user === null) {
			window.localStorage.removeItem(localStorageUserKey)
			state.userDetails = {}
		} else {
			window.localStorage.setItem(localStorageUserKey, JSON.stringify(user))
		}
	},
	SET_CURRENT_TOKEN (state, tokenViewModel) {
		if (state.currentUser) {
			setBearerToken(tokenViewModel.token)
			setTokenCookie(tokenViewModel.token)
			state.currentUser.tokenExpiry = tokenViewModel.tokenExpiry
			saveState('auth.currentUser', state.currentUser)
		}
	},
	SET_USER_DETAILS (state, userDetails) {
		state.userDetails = userDetails
		changeUserBrandIds(userDetails.brandIds)
	},
	SET_SESSION_WARNING (state, warning) {
		state.sessionWarning = warning
	},
	SET_COUNTDOWN (state, countdown) {
		state.countdown = countdown
	},
	SET_INSUFFICIENT_ACCESS (state, value) {
		state.insufficientAccess = value
	},
	SET_INSUFFICIENT_ACCESS_REASON (state, reason) {
		state.insufficientAccessReason = reason
	},
	SET_IS_REFRESHING_TOKEN (state, value) {
		state.isRefreshingToken = value
	}
}

export const getters = {
	loggedIn: state => state.currentUser?.userId != null,
	userPermissionsRetrieved (state) {
		return !!state.userDetails?.productPermissions
	},
	tokenExpiry: state => state.currentUser?.tokenExpiry,
	username (state) {
		return state.userDetails?.username
	},
	userFullName (state) {
		return state.userDetails?.fullName
	},
	userId (state) {
		return state.currentUser?.userId
	},
	productPermissions (state) {
		return state.userDetails?.productPermissions || []
	},
	hasPermission: (_, getters) => permissionId => {
		if (!getters.userId) return false
		return getters.productPermissions.includes(permissionId)
	},
	loggedInWithSSO (state) {
		return state.currentUser?.isSSO
	},
	userHasUniversalBrand (state) {
		return state.userDetails?.brandIds.includes(0)
	},
	sessionWarning (state) {
		return state.sessionWarning
	},
	countdown (state) {
		return state.countdown
	},
	hasInsufficientAccess (state) {
		return state.insufficientAccess
	},
	insufficientAccessReason (state) {
		return state.insufficientAccessReason
	}
}

export const actions = {
	ssoLogIn ({ dispatch }, { identityClaimTypeName, stateGuid, productId }) {
		return ssoLoginApi({ identityClaimTypeName, stateGuid, productId }).then(({ data }) => dispatch('handleLogin', { data: data, isSSO: true }))
	},
	logIn ({ commit, dispatch }, credentials) {
		return loginApi(credentials)
			.then(response => {
				if (response.status === 202) {
					localStorage.setItem('csMfaGuid', response.data.mfaGuid)
					return {
						otpLoginRequired: true
					}
				} else { dispatch('handleLogin', { data: response.data, isSSO: false }) }
			}
			)
			.catch(({ response }) => {
				if (response && response.status === 401) {
					commit('SET_CURRENT_USER', null)
					throw new Error(response.data.join())
				}
				if (response?.status === 403) {
					throw new Error(response.status)
				}
				throw new Error('There was a problem logging you in.')
			})
	},
	otpLogIn ({ commit, dispatch }, { otpCode, mfaGuid } = {}) {
		return configApi
			.post('/api/Authorize/CSPortalLoginOtp', { loginCode: otpCode, mfaGuid })
			.then(response => {
				dispatch('handleLogin', { data: response.data, isSSO: false })
				return response.data
			})
	},
	refreshToken ({ commit, state, dispatch }) {
		if (!state.currentUser) return Promise.resolve(null)
		return refreshTokenApi()
			.then(response => {
				const tokenViewModel = {
					token: response.data.token,
					tokenExpiry: parseToken(response.data.token).exp
				}
				commit('SET_CURRENT_TOKEN', tokenViewModel)
				dispatch('startSessionCheck')
				return tokenViewModel
			})
			.catch(error => {
				if (error.response && error.response.status === 401) {
					dispatch('logOut', SESSION_EXPIRED)
				} else {
					console.warn(error)
				}
				return null
			})
	},
	async handleLogin ({ commit, dispatch }, { data, isSSO }) {
		setBearerToken(data.token)
		setTokenCookie(data.token)
		const parsedToken = parseToken(data.token)
		const user = {
			tokenExpiry: parsedToken.exp,
			userId: parsedToken.userId,
			isSSO: isSSO
		}
		commit('SET_CURRENT_USER', user)
		dispatch('startSessionCheck')
		if (isSSO) {
			try {
				await dispatch('getUserDetails')
			} catch (error) {
				// If getUserDetails throws INSUFFICIENT_ACCESS error,
				// it will already have set the flags and the router guard will handle the redirect
				if (error.message === 'INSUFFICIENT_ACCESS') {
					return Promise.reject(error)
				}
				throw error
			}
		}
	},
	getBearerTokenFromCookie ({ commit, dispatch }) {
		const token = getCookieValue('cs-portal-token')
		if (token) {
			setBearerToken(token)
			const parsedToken = parseToken(token)
			const user = {
				userId: parsedToken.userId,
				tokenExpiry: parsedToken.exp
			}
			commit('SET_CURRENT_USER', user)
			dispatch('startSessionCheck')
		} else {
			commit('SET_CURRENT_USER', null)
		}
	},
	// Get sensitive user details.
	getUserDetails ({ commit, getters } = {}) {
		if (getters.loggedIn) {
			return currentUserDetailsApi()
				.then(({ data }) => {
					const userDetails = {
						username: data.currentUserDetails.userPreview.username,
						fullName: data.currentUserDetails.userPreview.fullName,
						brandIds: data.currentUserDetails.brandIds,
						productPermissions: data.currentUserDetails.permissionIds,
						userId: data.currentUserDetails.user.userId
					}
					// Check if user has no brands or no permissions
					const hasNoBrands = !userDetails.brandIds || userDetails.brandIds.length === 0
					const hasNoPermissions = !userDetails.productPermissions || userDetails.productPermissions.length === 0

					if (hasNoBrands || hasNoPermissions) {
						// Determine what's missing
						let reason = 'both'
						if (hasNoBrands && !hasNoPermissions) {
							reason = 'brands'
						} else if (!hasNoBrands && hasNoPermissions) {
							reason = 'permissions'
						}

						// Set flag FIRST before any other actions
						commit('SET_INSUFFICIENT_ACCESS', true)
						commit('SET_INSUFFICIENT_ACCESS_REASON', reason)
						commit('SET_CURRENT_USER', null)
						// Clear session artifacts
						setBearerToken(null)
						setTokenCookie(null)
						clearInterval(sessionCheckInterval)

						// Return rejected promise - router guard will handle navigation
						return Promise.reject(new Error('INSUFFICIENT_ACCESS'))
					}
					commit('SET_USER_DETAILS', userDetails)
					changeUserBrandIds(userDetails.brandIds)
					return userDetails
				})
				.catch(error => {
					console.warn(error)

					// Don't clear user or navigate for INSUFFICIENT_ACCESS - let router guard handle it
					if (error.message === 'INSUFFICIENT_ACCESS') {
						return Promise.reject(error)
					}

					commit('SET_CURRENT_USER', null)
					return Promise.resolve(null)
				})
		}
	},
	async logOut ({ commit, getters }, routeName) {
		clearInterval(sessionCheckInterval)
		const isSSO = getters.loggedInWithSSO
		const tenantGuid = sessionStorage.getItem('tenantGuid')
		const authHandler = sessionStorage.getItem('authHandler')

		if (routeName === SESSION_EXPIRED) {
			commit('SET_CURRENT_USER', null)
			setBearerToken(null)
			return
		}
		if (routeName == null) {
			routeName = 'login'
		}
		document.cookie = 'cs-portal-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
		if (isSSO && tenantGuid && authHandler) {
			sessionStorage.removeItem('tenantGuid')
			sessionStorage.removeItem('authHandler')
			window.location.href = `${authHandler}/signOut/${tenantGuid}`
			return
		}
		commit('SET_CURRENT_USER', null)
		setBearerToken(null)

		if (customLogoutUrl.value) {
			let logoutUrl = customLogoutUrl.value
			if (!logoutUrl.startsWith('http')) {
				logoutUrl = `https://${logoutUrl}`
			}
			window.location.href = logoutUrl
		} else {
			await router.push({ name: routeName })
			setTimeout(() => location.reload(), 250)
		}
	},
	startSessionCheck ({ dispatch }) {
		clearInterval(sessionCheckInterval)
		// Poll once a second and reconcile against the SHARED token cookie (see
		// checkSession). The per-second cadence also drives the warning countdown.
		sessionCheckInterval = setInterval(() => dispatch('checkSession'), 1000)
	},
	// Reconcile this tab against the shared token cookie, then drive the warning /
	// expiry. Runs on the session-check interval and on tab re-focus (AB#26125).
	// Using the cookie (not a stale in-memory expiry) means activity in any tab
	// keeps every tab alive, and a background tab can't tear down a session that
	// another tab has just refreshed.
	checkSession ({ state, commit, dispatch }) {
		if (!state.currentUser) {
			clearInterval(sessionCheckInterval)
			return
		}
		const cookieToken = getCookieValue('cs-portal-token')
		const result = decideSessionTick({
			cookieToken,
			knownExpirySeconds: state.currentUser.tokenExpiry,
			nowMs: Date.now()
		})
		if (result.kind === 'gone' || result.kind === 'expired') {
			dispatch('sessionExpired')
			return
		}
		// If the shared cookie now belongs to a DIFFERENT user (someone logged in on
		// another tab), re-initialise this tab from the cookie rather than running
		// under the new user's token while showing the previous user's cached
		// permissions. Checked independently of `adopt`: JWT `exp` has 1-second
		// granularity, so a same-second cross-user login can leave `exp` unchanged.
		if (result.userId != null && result.userId !== state.currentUser.userId) {
			window.location.reload()
			return
		}
		// Same user, token refreshed in another tab: adopt it in place.
		if (result.adopt) {
			setBearerToken(cookieToken)
			commit('SET_CURRENT_USER', { ...state.currentUser, tokenExpiry: result.expirySeconds })
		}
		if (result.kind === 'warning') {
			commit('SET_SESSION_WARNING', true)
			commit('SET_COUNTDOWN', Math.max(0, Math.floor(result.timeLeftMs / 1000)))
		} else if (state.sessionWarning) {
			commit('SET_SESSION_WARNING', false)
		}
	},
	sessionExpired ({ commit, dispatch, getters }) {
		const isSSO = getters.loggedInWithSSO
		if (isSSO) {
			sessionStorage.setItem('sso-ignore-return-path', 'true')
		}
		commit('SET_CURRENT_USER', null)
		commit('SET_SESSION_WARNING', false)
		dispatch('logOut', SESSION_EXPIRED)
		router.push({ name: SESSION_EXPIRED })
	},
	// Called when there's API activity to indicate user is active
	// This proactively refreshes the token if approaching expiry
	recordActivity ({ state, commit, dispatch }) {
		// Only proceed if user is logged in and not already refreshing
		if (!state.currentUser || state.isRefreshingToken) return

		const expiry = new Date(0)
		expiry.setUTCSeconds(state.currentUser.tokenExpiry)
		const now = new Date()
		const timeLeft = expiry - now

		// If we're within 12 minutes of expiry (15 min timeout - 3 min buffer), proactively refresh the token
		// This prevents the session warning from appearing during active use
		const REFRESH_THRESHOLD = 720000 // 12 minutes in milliseconds (CS Portal has 15 min timeout)
		if (timeLeft <= REFRESH_THRESHOLD && timeLeft > 0) {
			// Clear any existing warning first
			if (state.sessionWarning) {
				commit('SET_SESSION_WARNING', false)
			}
			// Set flag to prevent concurrent refreshes
			commit('SET_IS_REFRESHING_TOKEN', true)
			// Refresh the token to extend the session
			dispatch('refreshToken').finally(() => {
				commit('SET_IS_REFRESHING_TOKEN', false)
			})
		} else if (state.sessionWarning) {
			// If there's a warning showing but we're not close to expiry, clear it
			commit('SET_SESSION_WARNING', false)
		}
	}
}
